import axiosInstance from "./axiosInstanceV1";

/**
 * User API functions for BodhiStreams
 * These are "heavy" endpoints that hit the database.
 */

/**
 * Fetch full user profile (DB query with membership + stripe joins)
 * @returns {Promise<Object>} Full user profile data
 */
export const fetchUserProfile = async () => {
	const response = await axiosInstance.get("/user/users/profile");
	return response.data;
};

/**
 * Update current user profile
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user profile
 */
export const updateUserProfile = async (userData) => {
	const response = await axiosInstance.patch("/user/users/profile", userData);
	return response.data;
};

/**
 * Fetch all users (admin only)
 * @returns {Promise<Array>} Array of users
 */
export const fetchUsers = async () => {
	// The endpoint pages at 50 by default; the admin list filters client-side,
	// so ask for everyone or users past the first page are unsearchable.
	const response = await axiosInstance.get("/admin/users/list", {
		params: { per_page: 10000 },
	});
	return response.data.users || [];
};

/**
 * Fetch users count (admin only)
 * @returns {Promise<number>} Number of users
 */
export const fetchUsersCount = async () => {
	const response = await axiosInstance.get("/admin/users/count");
	return response.data.count;
};

/**
 * Fetch full admin details for one user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Admin user detail payload
 */
export const fetchAdminUserDetails = async (userId) => {
	const response = await axiosInstance.get(`/admin/users/${userId}`);
	return response.data;
};

/**
 * Delete a user (admin only)
 * @param {string} userId - User ID to delete
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
	await axiosInstance.delete(`/admin/users/${userId}`);
};

/**
 * Create a new user (admin only)
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (userData) => {
	// Transform membership field to membership_id for the API
	const apiData = { ...userData };
	if (apiData.membership !== undefined) {
		apiData.membership_id = apiData.membership;
		delete apiData.membership;
	}

	const response = await axiosInstance.post("/admin/users/list", apiData);
	return response.data;
};

/**
 * Update a user (admin only)
 * @param {string} userId - User ID to update
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (userId, userData) => {
	// Transform membership field to membership_id for the API
	const apiData = { ...userData };
	if (apiData.membership !== undefined) {
		apiData.membership_id = apiData.membership;
		delete apiData.membership;
	}

	const response = await axiosInstance.patch(`/admin/users/${userId}`, apiData);
	return response.data;
};

/**
 * Upload a profile picture
 * @param {File} file - The image file to upload
 * @returns {Promise<{profile_picture_url: string}>}
 */
export const uploadProfilePicture = async (file) => {
	const formData = new FormData();
	formData.append("file", file);
	const response = await axiosInstance.post(
		"/user/users/profile/avatar",
		formData,
		{
			headers: {
				"Content-Type": undefined,
			},
		},
	);
	return response.data;
};

/**
 * Delete the current profile picture
 * @returns {Promise<void>}
 */
export const deleteProfilePicture = async () => {
	await axiosInstance.delete("/user/users/profile/avatar");
};

/**
 * Request deletion of the authenticated user's own account.
 * Deletion does NOT happen immediately — the backend emails a single-use
 * confirmation link (24h expiry); the account is only removed when the user
 * confirms via `confirmAccountDeletion` (called by the landing page the
 * emailed link opens).
 * @returns {Promise<{message: string}>}
 */
export const requestAccountDeletion = async () => {
	const response = await axiosInstance.post(
		"/user/users/account/deletion-request",
	);
	return response.data;
};

/**
 * Confirm account deletion with the token from the deletion email.
 * Public endpoint — the token is the authority, not a session.
 * @param {string} token - Deletion token from the emailed link
 * @returns {Promise<{message: string}>}
 */
export const confirmAccountDeletion = async (token) => {
	const response = await axiosInstance.post(
		"/public/users/account/deletion-confirm",
		{ token },
	);
	return response.data;
};
